import { getLocalization, formatCurrencyWithSymbol } from 'base/lib/localization'
import store from 'base/store'

export function getShippingCutoffNote(shippingOptions) {
  const selectedOption = shippingOptions.find(option => option.selected)
  const cutoffDate = new Date(selectedOption.shipping_cutoff)
  const formattedCutoffTime = cutoffDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })

  return `Orders placed after ${formattedCutoffTime} will be shipped the following business day.`
}

export function itemsNeededForFreeDomesticShipping({
  shippingOptions,
  cartSize,
  userHasFreeShipping,
  country,
}) {
  const containsFreeOption = shippingOptions.some(option => option.amount === '$0')
  const showFreeShippingCopy =
    !containsFreeOption && cartSize < 2 && !userHasFreeShipping && country === 'US'

  if (showFreeShippingCopy) {
    return 2 - cartSize
  }

  return null
}

export function costNeededForFreeDomesticShipping({
  shippingOptions,
  cartTotal,
  userHasFreeShipping,
  country,
}) {
  const containsThresholdAmount = shippingOptions.find(option => option.threshold_amount !== null)

  const costNeeded = containsThresholdAmount ? containsThresholdAmount.threshold_amount : null

  const showFreeShippingCopy =
    costNeeded && cartTotal < costNeeded && !userHasFreeShipping && country === 'US'

  if (showFreeShippingCopy) {
    return costNeeded - cartTotal
  }

  return null
}

export function costNeededForFreeInternationalStandardShipping({
  addressCountry,
  localeCountry,
  cartTotal,
  intlUserHasFreeShippingCoupon,
  userFirstOrder,
}) {
  const activeRate = determineActiveRate(addressCountry, localeCountry)

  if (!activeRate) {
    return null
  }

  const standardRateNeededForFreeShipping = activeRate.free_standard_shipping_threshold - cartTotal

  const intlUserHasFreeShippingOnFirstOrder =
    userFirstOrder &&
    activeRate.free_shipping_on_first_order &&
    store.getState().appConfig.intl_first_order_free_shipping_enabled

  const showFreeStandardShippingIntlCopy =
    !intlUserHasFreeShippingCoupon &&
    !intlUserHasFreeShippingOnFirstOrder &&
    activeRate.free_standard_shipping_threshold &&
    standardRateNeededForFreeShipping > 0

  if (showFreeStandardShippingIntlCopy) {
    return standardRateNeededForFreeShipping
  }

  return null
}

export function costNeededForFreeInternationalExpressShipping({
  addressCountry,
  localeCountry,
  cartTotal,
  intlUserHasFreeShippingCoupon,
  userFirstOrder,
}) {
  const activeRate = determineActiveRate(addressCountry, localeCountry)

  if (!activeRate) {
    return null
  }

  const hasFreeStandardCoupon =
    intlUserHasFreeShippingCoupon && activeRate.free_standard_shipping_threshold

  const hasFreeExpressOnlyCoupon =
    !activeRate.free_standard_shipping_threshold && intlUserHasFreeShippingCoupon

  const hasFreeStandardShipping =
    activeRate.free_standard_shipping_threshold < cartTotal || hasFreeStandardCoupon

  const expressRateNeededForFreeShipping = activeRate.free_express_shipping_threshold - cartTotal

  const showFreeExpressShippingIntlCopy =
    activeRate.free_express_shipping_threshold &&
    expressRateNeededForFreeShipping > 0 &&
    !hasFreeExpressOnlyCoupon &&
    (hasFreeStandardShipping || !intlUserHasFreeShippingCoupon) &&
    !costNeededForFreeInternationalStandardShipping({
      addressCountry,
      localeCountry,
      cartTotal,
      intlUserHasFreeShippingCoupon,
      userFirstOrder,
    })

  if (showFreeExpressShippingIntlCopy) {
    return expressRateNeededForFreeShipping
  }

  return null
}

export function costNeededForReducedInternationalExpressShipping({
  addressCountry,
  localeCountry,
  cartTotal,
  intlUserHasFreeShippingCoupon,
}) {
  const activeRate = determineActiveRate(addressCountry, localeCountry)

  if (!activeRate) {
    return null
  }

  const hasFreeExpressOnlyCoupon =
    !activeRate.free_standard_shipping_threshold && intlUserHasFreeShippingCoupon

  const highValueRateNeededForFreeShipping = activeRate.high_value_threshold - cartTotal

  const showReducedExpressShippingIntlCopy =
    activeRate.high_value_threshold &&
    activeRate.high_value_rate &&
    highValueRateNeededForFreeShipping > 0 &&
    !hasFreeExpressOnlyCoupon

  if (showReducedExpressShippingIntlCopy) {
    return highValueRateNeededForFreeShipping
  }

  return null
}

export function isFreeExpressShippingOffered({ addressCountry, localeCountry }) {
  const activeRate = determineActiveRate(addressCountry, localeCountry)

  if (activeRate && activeRate.free_express_shipping_threshold) {
    return true
  }
  return false
}

function thresholdForFreeDomesticShipping({ shippingOptions, country }) {
  const containsThresholdAmount = shippingOptions.find(option => option.threshold_amount !== null)
  const costNeeded = containsThresholdAmount?.threshold_amount

  if (costNeeded && country === 'US') {
    return costNeeded
  }

  return null
}

export function thresholdForFreeInternationalShipping({ addressCountry, localeCountry }) {
  const activeRate = determineActiveRate(addressCountry, localeCountry)

  if (activeRate?.free_standard_shipping_threshold) {
    return activeRate.free_standard_shipping_threshold
  }

  return null
}

export function getFreeShippingThreshold({ shippingOptions, addressCountry, localeCountry }) {
  const costForFreeDomesticShipping = thresholdForFreeDomesticShipping({
    shippingOptions,
    country: addressCountry,
  })

  if (costForFreeDomesticShipping) {
    return costForFreeDomesticShipping
  }

  const costForFreeInternationalShipping = thresholdForFreeInternationalShipping({
    addressCountry,
    localeCountry,
  })

  if (costForFreeInternationalShipping) {
    return costForFreeInternationalShipping
  }
}

export function getFreeShippingThresholdCopy({
  shippingOptions,
  cartTotal,
  addressCountry,
  localeCountry,
  userHasFreeShipping,
  intlUserHasFreeShippingCoupon,
  userFirstOrder,
}) {
  const costForFreeDomesticShipping = stubbableFunctions.costNeededForFreeDomesticShipping({
    shippingOptions,
    cartTotal,
    userHasFreeShipping,
    country: addressCountry,
  })

  if (costForFreeDomesticShipping) {
    return `You’re $${costForFreeDomesticShipping}
      away from free shipping.`
  }

  const intlParams = {
    addressCountry,
    localeCountry,
    cartTotal,
    intlUserHasFreeShippingCoupon,
    userFirstOrder,
  }
  const deficitForFreeInternationalStandardShipping =
    stubbableFunctions.costNeededForFreeInternationalStandardShipping(intlParams)

  if (deficitForFreeInternationalStandardShipping) {
    return `You’re ${formatCurrencyWithSymbol(
      deficitForFreeInternationalStandardShipping,
    )} away from free standard shipping.`
  }

  const deficitForFreeInternationalExpressShipping =
    stubbableFunctions.costNeededForFreeInternationalExpressShipping(intlParams)

  if (deficitForFreeInternationalExpressShipping) {
    return `You’re ${formatCurrencyWithSymbol(
      deficitForFreeInternationalExpressShipping,
    )} away from free express shipping.`
  }

  const freeExpressCheckParams = {
    addressCountry,
    localeCountry,
  }
  const isFreeExpressShippingOffered =
    stubbableFunctions.isFreeExpressShippingOffered(freeExpressCheckParams)
  if (isFreeExpressShippingOffered) {
    return null
  }

  const deficitForReducedInternationalExpressShipping =
    stubbableFunctions.costNeededForReducedInternationalExpressShipping(intlParams)

  if (deficitForReducedInternationalExpressShipping) {
    const activeRate = determineActiveRate(addressCountry, localeCountry)
    const highValueRate = Number.parseInt(activeRate.high_value_rate, 10)

    return `You’re ${formatCurrencyWithSymbol(
      deficitForReducedInternationalExpressShipping,
    )} away from ${getLocalization().symbol}${highValueRate} express shipping.`
  }
}

export const canUseLocalRate = (addressCountry, localeCountry) => {
  const localShippingConfig = store.getState().shippingConstants.local_shipping_configuration

  const countriesMatch = !!(addressCountry === localeCountry)
  const hasLocalRate = localShippingConfig && !!localShippingConfig[addressCountry]

  return hasLocalRate && countriesMatch
}

export const determineActiveRate = (addressCountry, localeCountry) => {
  const shippingConstants = store.getState().shippingConstants

  const { local_shipping_configuration, international_shipping_configuration } =
    shippingConstants || {}

  if (canUseLocalRate(addressCountry, localeCountry)) {
    return local_shipping_configuration && local_shipping_configuration[addressCountry]
  } else {
    return (
      international_shipping_configuration && international_shipping_configuration[addressCountry]
    )
  }
}

export const stubbableFunctions = {
  getShippingCutoffNote,
  itemsNeededForFreeDomesticShipping,
  costNeededForFreeDomesticShipping,
  costNeededForFreeInternationalStandardShipping,
  costNeededForFreeInternationalExpressShipping,
  costNeededForReducedInternationalExpressShipping,
  isFreeExpressShippingOffered,
  getFreeShippingThresholdCopy,
}
